import { type PointerEvent as ReactPointerEvent, useCallback, useRef } from "react";

import { CARRY_THRESHOLD_PX } from "./useCarryGesture";

/**
 * A tile that can be clicked *or* picked up.
 *
 * `press` goes on `onPointerDown` and begins a carry once the pointer has
 * travelled as far as turns a click into a drag anywhere else; `click` wraps
 * the tile's own `onClick` and swallows the one the browser sends when a drag
 * is let go of back over the tile it started on — that release puts the carry
 * down, and a click arming it again would undo it.
 *
 * It only *starts* the carry. From there the pointer is `useCarryGesture`'s,
 * which is what makes a tile and a piece on the board the same drag.
 */
export function useTileCarry() {
  const dragged = useRef(false);

  const press = useCallback(
    (begin: (at: { x: number; y: number }) => void) => (event: ReactPointerEvent) => {
      dragged.current = false;
      if (event.button !== 0) return;
      let { clientX: x, clientY: y } = event;
      let travel = 0;
      const stop = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", stop);
        window.removeEventListener("pointercancel", stop);
      };
      const move = (next: PointerEvent) => {
        travel += Math.abs(next.clientX - x) + Math.abs(next.clientY - y);
        x = next.clientX;
        y = next.clientY;
        if (travel <= CARRY_THRESHOLD_PX) return;
        stop();
        dragged.current = true;
        begin({ x, y });
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", stop);
      window.addEventListener("pointercancel", stop);
    },
    []
  );

  const click = useCallback(
    (run: () => void) => () => {
      if (dragged.current) dragged.current = false;
      else run();
    },
    []
  );

  return { press, click };
}
